import {OptionsService} from "../service/OptionsService";
import moment from "moment";
import {SecurityMap, SecurityMapOf} from "app/security/common/map/SecurityMap";
import {OptionChainRecord, OptionRecord, OptionRecords, OptionSeriesRecord} from "../models/OptionChainRecord";
import {CodeAndExchange} from "../../../instruments/types/CodeAndExchange";
import {flatMap} from "../../../js/util/UtilFunctions";

export class OptionContractsCache {

    public static readonly id = "optionContractsCache";

    private lastRequest = moment().subtract(1, "years");
    private cache: Promise<SecurityMap<OptionRecord>>;

    /** @ngInject */
    public constructor(private readonly optionsService: OptionsService) {
    }

    public async all(): Promise<OptionRecords> {
        this.cache = this.refreshCache();
        const cache = await this.cache;
        return cache.values();
    }

    public async find(security: CodeAndExchange): Promise<OptionRecord | null> {
        this.cache = this.refreshCache();
        const cache = await this.cache;
        return cache.get(security);
    }

    private async refreshCache(): Promise<SecurityMap<OptionRecord>> {
        if ((this.cache != null) && this.cacheValid()) {
            return this.cache;
        }
        this.lastRequest = moment();
        try {
            const chains = await this.optionsService.retrieve();
            const options = chains.map((c) => this.chainOptions(c))
                                  .reduce(flatMap, []);
            const cache = SecurityMapOf<OptionRecord>();
            options.forEach((o) => cache.set(o, o));
            return cache;
        } catch (e) {
            this.lastRequest = moment().subtract(1, "years");
            throw e;
        }
    }

    /* Cache is invalid if it is over an hour since the last refresh */
    private cacheValid(): boolean {
        const now = moment();
        const diff = now.diff(this.lastRequest, "hour");
        return Math.abs(diff) < 1;
    }

    private chainOptions(chain: OptionChainRecord): OptionRecords {
        return chain.series.map((s) => this.seriesOptions(s)).reduce(flatMap, []);
    }

    private seriesOptions(series: OptionSeriesRecord): OptionRecords {
        return [...series.puts, ...series.calls];
    }
}
